import { useMemo } from 'react';
import { useAuthStore } from '@/lib/auth/store';
import { usePermissionStore } from './store';
import type { MODULE_LISTING_KEYS } from '@/lib/constants/modules';

type ListingKey = (typeof MODULE_LISTING_KEYS)[keyof typeof MODULE_LISTING_KEYS];

/**
 * Filters an array based on the sub-super-admin's moduleListingData.
 *
 * - Non sub-super-admin → returns data as-is.
 * - moduleListing is {} (no restrictions defined) → returns data as-is.
 * - moduleListing[key] is undefined → this module not restricted → data as-is.
 * - moduleListing[key] is [] (empty array) → no access → returns [].
 * - moduleListing[key] has IDs → filters by those IDs only.
 */
export function useModuleFilter<T>(
  data: T[],
  key: ListingKey,
  idExtractor: (item: T) => number | string,
): T[] {
  const isSub = useAuthStore((s) => s.isSubSuperAdmin());
  const listing = usePermissionStore((s) => s.moduleListing);

  return useMemo(() => {
    if (!isSub) return data;
    if (Object.keys(listing).length === 0) return data;

    const allowed = listing[key];
    if (allowed === undefined) return data;
    if (!Array.isArray(allowed)) return data;
    if (allowed.length === 0) return [];

    const allowedSet = new Set(allowed.map(Number));
    return data.filter((item) => allowedSet.has(Number(idExtractor(item))));
  }, [data, isSub, listing, key, idExtractor]);
}